/**
 * The ocean swell — an animated, seamlessly-tiling pattern painted under the
 * imagery wherever the baked texture leaves the sea translucent.
 *
 * WHY A PATTERN AND NOT A SHADER. MapLibre has no blend modes and no hook for
 * a custom ocean shader that would survive globe projection, but a
 * `background` layer with `background-pattern` gives a MAP-ANCHORED texture —
 * it rotates with the planet instead of sliding across the lens, which is the
 * difference between an ocean and a filter — and the pattern's pixels can be
 * rewritten at runtime. That is the whole animation, for the cost of one small
 * texture upload per tick and not one line of GL.
 *
 * WHY `advance()` AND NOT `StyleImageInterface.render()`. MapLibre documents
 * `render()` as "called once before every frame where the icon will be used",
 * and for a background pattern it is never called at all. The dispatch lives on
 * `ImageAtlas.patchUpdatedImages` (`maplibre-gl@5.24.0`), which is built PER
 * TILE by the worker for layers that carry icons or fill/line patterns; a
 * background layer has no tile and therefore no atlas, so nothing ever asks.
 * `drawBackground` instead reads `imageManager.getPattern()` every frame, and
 * that re-copies the image into the pattern atlas whenever `image.version` has
 * moved. `Map.updateImage()` is what moves it. So the ticker in `Globe.tsx`
 * calls `advance()` and pushes the result itself.
 *
 * WHY IT IS UNDER THE IMAGERY. There is no ocean polygon in this style and
 * building one (a world rect with ~4,000 country rings punched out) would cost
 * an earcut over the whole planet. The imagery already knows where the sea is,
 * to the pixel, because the bake painted it: skins with `oceanAlpha < 1` leave
 * the sea translucent and the land fully opaque. Sliding the swell beneath the
 * raster therefore masks it to open water exactly, for free. See
 * `packages/data/scripts/fetchearth.ts`.
 *
 * INVARIANT 7 — this adds no `glyphs`, no `sprite` and no `symbol` layer. The
 * image is generated arithmetically in this file and handed to
 * `map.addImage()`; nothing is fetched, and a pattern cannot render text.
 *
 * SEAMLESSNESS is arithmetic, not artistry: every component's wave numbers are
 * INTEGERS, so each one completes a whole number of cycles across the tile in
 * both axes and the tile's right edge is its left edge by construction. Any
 * non-integer wave number would put a visible grid of seams across the ocean.
 */

/** The `map.addImage()` id. */
export const WAVE_IMAGE_ID = 'geogame-swell';

/**
 * Pattern size in texels. Power of two so the pattern atlas can mip it, and
 * small enough that a full rewrite per frame is a rounding error: 128x128 RGBA
 * is 64 KiB, against the ~10 MiB of Earth texture already resident.
 */
export const WAVE_TILE_PX = 128;

/**
 * Texels per CSS pixel the pattern is drawn at.
 *
 * 2 makes a `WAVE_TILE_PX` tile cover 64 CSS px, which at the home view puts
 * roughly a dozen crests across the visible ocean — fine enough to read as
 * surface rather than as a paint effect, coarse enough to survive the limb.
 */
export const WAVE_PIXEL_RATIO = 2;

/**
 * Swell components. `nx`/`ny` are cycles across the tile (INTEGERS — see the
 * header), `amp` is relative height and `hz` is drift in cycles per second;
 * opposing signs make the components beat against each other instead of
 * marching in step, which is what stops the field reading as wallpaper.
 *
 * Four components is the knee. Three leaves a visible dominant direction;
 * five costs another two tables per frame and no one can tell.
 */
const SWELL: ReadonlyArray<{ nx: number; ny: number; amp: number; hz: number }> = [
  // Two nearly-parallel primaries. Nearly, not exactly: their beat is what
  // makes a crest fade out along its own length instead of ringing the tile
  // like a contour line.
  { nx: 6, ny: 2, amp: 1.0, hz: 0.026 },
  { nx: 7, ny: 3, amp: 0.78, hz: -0.019 },
  // Cross swell, running across the primaries, breaks up the corduroy.
  { nx: -3, ny: 8, amp: 0.34, hz: 0.041 },
  // Chop. Small and fast; it is what keeps the surface from looking poured.
  { nx: 13, ny: 5, amp: 0.14, hz: -0.077 },
];

/**
 * The envelope: two very long components that MULTIPLY the crest field rather
 * than adding to it.
 *
 * Without it the ocean is uniform corduroy and the 128-texel tile is plainly
 * visible as a repeating diagonal grid. With it the same crests have quiet
 * water and choppy water, and the repeat stops being the thing the eye
 * notices, because the pattern it repeats is no longer uniform. It is also the
 * only part of the field that ever fully calms the sea, which is the shape
 * real water has.
 */
const ENVELOPE: ReadonlyArray<{ nx: number; ny: number; amp: number; hz: number }> = [
  { nx: 1, ny: 1, amp: 1.0, hz: 0.009 },
  { nx: -2, ny: 1, amp: 0.68, hz: -0.006 },
];

/** How far the envelope is allowed to knock the swell down. */
const ENVELOPE_FLOOR = 0.24;

const ALL = [...SWELL, ...ENVELOPE];
const SWELL_N = SWELL.length;
const AMP_TOTAL = SWELL.reduce((n, s) => n + s.amp, 0);
const ENVELOPE_TOTAL = ENVELOPE.reduce((n, s) => n + s.amp, 0);

/**
 * Crest and trough colour, and how much of each the pattern is allowed to
 * contribute.
 *
 * The alphas are LOW on purpose. The swell is seen through the translucent sea
 * of the imagery above it, so what lands on screen is roughly a third of what
 * is painted here; anything strong enough to look right in isolation reads as
 * a scratched lens on the globe.
 */
const CREST: readonly [number, number, number] = [176, 214, 240];
const TROUGH: readonly [number, number, number] = [4, 15, 30];
const CREST_ALPHA = 0.34;
const TROUGH_ALPHA = 0.3;

/**
 * Where a crest starts and where it saturates, as a fraction of peak height.
 *
 * The gap between them is the whole look. Widen it and the ocean becomes a
 * smooth sine wash with no waves in it; narrow it and the crests turn into
 * hard-edged worms. Starting well above zero is what keeps the crests THIN —
 * most of the ocean is neither crest nor trough, which is true of oceans.
 */
const CREST_IN = 0.38;
const CREST_OUT = 0.98;
const TROUGH_IN = 0.3;
const TROUGH_OUT = 0.92;

/** Hermite smoothstep, clamped. */
function smoothstep(edge0: number, edge1: number, x: number): number {
  const t = Math.min(1, Math.max(0, (x - edge0) / (edge1 - edge0)));
  return t * t * (3 - 2 * t);
}

/**
 * The per-pixel, per-component phase tables.
 *
 * Precomputed because the whole animation is one trigonometric identity:
 *
 *     sin(p + wt) = sin(p)cos(wt) + cos(p)sin(wt)
 *
 * so a frame costs `SWELL.length` multiply-adds per pixel and exactly two
 * `Math.sin` calls per component per frame, instead of 65,536 of them. That is
 * the difference between a 0.05 ms frame and a 3 ms one on a phone.
 */
interface WaveTables {
  readonly size: number;
  readonly sin: Float32Array[];
  readonly cos: Float32Array[];
}

export function waveTables(size: number): WaveTables {
  const n = size * size;
  const sin: Float32Array[] = [];
  const cos: Float32Array[] = [];
  for (const s of ALL) {
    const a = new Float32Array(n);
    const b = new Float32Array(n);
    for (let y = 0; y < size; y += 1) {
      for (let x = 0; x < size; x += 1) {
        const phase = 2 * Math.PI * ((s.nx * x) / size + (s.ny * y) / size);
        const i = y * size + x;
        a[i] = Math.sin(phase);
        b[i] = Math.cos(phase);
      }
    }
    sin.push(a);
    cos.push(b);
  }
  return { size, sin, cos };
}

/**
 * Write one frame of the swell into `out` (RGBA, NOT premultiplied — MapLibre
 * premultiplies on upload).
 *
 * Exported and pure so `waves.test.ts` can assert the two properties that
 * actually matter: the tile wraps seamlessly, and the field stays inside its
 * alpha budget at every phase.
 */
export function waveFrame(tables: WaveTables, out: Uint8Array, seconds: number): void {
  const { size, sin, cos } = tables;
  const n = size * size;
  // Per-component rotation of the phase, evaluated once per frame.
  const cs: number[] = [];
  const sn: number[] = [];
  for (const s of ALL) {
    const wt = 2 * Math.PI * s.hz * seconds;
    cs.push(Math.cos(wt));
    sn.push(Math.sin(wt));
  }

  for (let i = 0; i < n; i += 1) {
    let h = 0;
    for (let k = 0; k < SWELL_N; k += 1) {
      h += SWELL[k]!.amp * (sin[k]![i]! * cs[k]! + cos[k]![i]! * sn[k]!);
    }
    h /= AMP_TOTAL;

    let e = 0;
    for (let k = 0; k < ENVELOPE.length; k += 1) {
      const j = SWELL_N + k;
      e += ENVELOPE[k]!.amp * (sin[j]![i]! * cs[j]! + cos[j]![i]! * sn[j]!);
    }
    // -1..1 -> ENVELOPE_FLOOR..1
    const env = ENVELOPE_FLOOR + (1 - ENVELOPE_FLOOR) * (0.5 + e / (2 * ENVELOPE_TOTAL));

    const p = i * 4;
    if (h >= 0) {
      const a = smoothstep(CREST_IN, CREST_OUT, h) * CREST_ALPHA * env;
      out[p] = CREST[0];
      out[p + 1] = CREST[1];
      out[p + 2] = CREST[2];
      out[p + 3] = Math.round(a * 255);
    } else {
      const a = smoothstep(TROUGH_IN, TROUGH_OUT, -h) * TROUGH_ALPHA * env;
      out[p] = TROUGH[0];
      out[p + 1] = TROUGH[1];
      out[p + 2] = TROUGH[2];
      out[p + 3] = Math.round(a * 255);
    }
  }
}

/**
 * The object handed to `map.addImage()`, and re-handed to `map.updateImage()`
 * every time `advance()` says there are new pixels.
 *
 * Two things it deliberately does NOT do:
 *
 *  - it does not request a repaint. An image cannot drive the render loop, and
 *    one that tried would keep a static globe awake forever. `Globe.tsx` owns
 *    the ticker and only runs it while the swell can actually be seen.
 *  - it does not animate under `prefers-reduced-motion`. The crest field is
 *    still drawn — a still ocean with texture in it is the point — but it is
 *    frozen at t = 0, so nothing on the screen moves.
 *
 * Frames are throttled to `fps`, and that number is also the globe's repaint
 * rate while the swell is running, because every new frame costs a
 * `triggerRepaint`. Ocean swell is slow: 15 is indistinguishable from 60 on a
 * field whose fastest component drifts at 0.077 Hz, and it is a quarter of the
 * battery.
 */
export interface WaveImage {
  readonly width: number;
  readonly height: number;
  readonly data: Uint8Array;
  /**
   * Write the next frame if the throttle allows it.
   *
   * @returns `true` when `data` changed and the caller must push it to the map.
   */
  advance(): boolean;
  /** Freeze or resume the drift. Set from `prefers-reduced-motion`. */
  setAnimated(on: boolean): void;
}

export interface WaveImageOptions {
  readonly size?: number;
  readonly fps?: number;
  readonly animated?: boolean;
  /** Injectable clock, in milliseconds. Defaults to `performance.now()`. */
  readonly now?: () => number;
}

export function createWaveImage(opts: WaveImageOptions = {}): WaveImage {
  const size = opts.size ?? WAVE_TILE_PX;
  const fps = opts.fps ?? 15;
  const now = opts.now ?? (() => performance.now());
  const tables = waveTables(size);
  const data = new Uint8Array(size * size * 4);

  let animated = opts.animated !== false;
  let startedAt = now();
  // Seeded to construction time, not -Infinity: the constructor has already
  // painted t = 0 below, so the first `advance()` is throttled like every other
  // one rather than repainting the same field a millisecond later.
  let lastFrameAt = startedAt;
  /** Drift accumulated so far. Kept across freezes so resuming does not jump. */
  let seconds = 0;

  waveFrame(tables, data, 0);

  return {
    width: size,
    height: size,
    data,
    advance(): boolean {
      if (!animated) return false;
      const t = now();
      if (t - lastFrameAt < 1000 / fps) return false;
      lastFrameAt = t;
      seconds = (t - startedAt) / 1000;
      waveFrame(tables, data, seconds);
      return true;
    },
    setAnimated(on: boolean): void {
      if (on === animated) return;
      animated = on;
      // Rebase the clock so the swell picks up where it stopped.
      if (on) startedAt = now() - seconds * 1000;
    },
  };
}
